import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import GoogleLogo from '../../assets/images/google-logo.svg'
import LinkedInLogo from '../../assets/images/linkedin-logo.svg'
import MicrosoftLogo from '../../assets/images/microsoft-logo.svg'
import { useMsal } from '@azure/msal-react'
import { EventType } from '@azure/msal-browser'
import { useCompanySso } from '../../common/companySso'
import { useRewardfulState } from '../../rewardful/hooks'
import './Auth.scss'

export interface SocialLogin {
  identityProvider: string
  displayName: string
  redirectUri: string
  responseType: string
  clientId: string
  scope: string
  logo: string
}

const COGNITO_DOMAIN = process.env.REACT_APP_CONSOLE_COGNITO_DOMAIN
const AUTH_DOMAIN = process.env.REACT_APP_AUTH_API_BASE_URL
const CLIENT_ID = process.env.REACT_APP_CONSOLE_COGNITO_CLIENT_ID!

export const SocialButtons: React.FC<{ buttonText: string }> = ({ buttonText }) => {
  const { instance } = useMsal()
  const [searchParams] = useSearchParams()
  const code = searchParams.get('state')
  const bdSource = searchParams.get('bd_source')
  const sourceClient = searchParams.get('sourceClient')
  const [loading, setLoading] = useState<boolean>(false)
  const socialLogins: SocialLogin[] = [
    {
      identityProvider: 'Google',
      displayName: 'Google',
      redirectUri: `${AUTH_DOMAIN}/v2/console/oauth2/idpresponse`,
      responseType: 'CODE',
      clientId: CLIENT_ID,
      scope: 'email+openid+phone+profile',
      logo: GoogleLogo,
    },
    {
      identityProvider: 'LinkedIn',
      displayName: 'LinkedIn',
      redirectUri: `${AUTH_DOMAIN}/v2/console/oauth2/idpresponse`,
      responseType: 'CODE',
      clientId: CLIENT_ID,
      scope: 'email+openid+phone+profile',
      logo: LinkedInLogo,
    },
  ]
  const { shouldUseCompanySso, savePreferredAuthMethodCandidate, getCompanySpecificIdPs } = useCompanySso()
  let stateBase: { bd_source: string | null; code: string | null; sourceClient?: string | null } = { bd_source: bdSource, code: code }
  if (sourceClient) {
    stateBase.sourceClient = sourceClient
  }
  let state = useRewardfulState(stateBase, true)

  const authenticateWithMsal = async () => {
    setLoading(true)
    instance.loginRedirect({ scopes: ['User.Read'], prompt: 'select_account' }).catch(console.error)
  }

  useEffect(() => {
    const callbackId = instance.addEventCallback((message) => {
      if (
        message.eventType === EventType.LOGIN_SUCCESS ||
        message.eventType === EventType.SSO_SILENT_SUCCESS ||
        message.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        const result = message.payload
        if (result && 'idToken' in result) {
          setLoading(true)
          const stateQuery = state ? `&state=${state}` : ''
          window.location.href = `${AUTH_DOMAIN}/v2/console/oauth2/msalresponse?idToken=${result.idToken}${stateQuery}`
        }
      }
    })

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId)
      }
    }
  }, [state, instance])

  const msButtonContent = (
    <>
      <img src={MicrosoftLogo} className='auth-button-logo' alt='Microsoft logo' />
      <span className='auth-button-text'>{`${buttonText}Microsoft`}</span>
    </>
  )

  const socialButtonSelected = (identityProvider: string) => {
    setLoading(true)
    const redirectUrl = searchParams.get('redirect')
    redirectUrl && localStorage.setItem('socialLoginRedirect', redirectUrl)
    savePreferredAuthMethodCandidate(identityProvider)
  }
  return (
    <>
      {(shouldUseCompanySso() ? getCompanySpecificIdPs() : socialLogins).map(
        ({ identityProvider, displayName, redirectUri, responseType, clientId, scope, logo }) => {
          const stateQuery = state ? `&state=${state}` : ''
          const searchParams = `identity_provider=${identityProvider}&redirect_uri=${redirectUri}&response_type=${responseType}&client_id=${clientId}&scope=${scope}${stateQuery}`
          const buttonContent = (
            <>
              <img src={logo} className='auth-button-logo' alt={`${identityProvider} logo`} />
              <span className='auth-button-text'>{`${buttonText}${displayName}`}</span>
            </>
          )
          return loading ? (
            <div key={identityProvider} className='social-button social-button-disabled'>
              {buttonContent}
            </div>
          ) : (
            <a
              key={identityProvider}
              className='social-button'
              href={`${COGNITO_DOMAIN}?${searchParams}`}
              onClick={() => socialButtonSelected(identityProvider)}
            >
              {buttonContent}
            </a>
          )
        },
      )}
      {!shouldUseCompanySso() &&
        (loading ? (
          <div key='Microsoft' className='social-button social-button-disabled'>
            {msButtonContent}
          </div>
        ) : (
          <div key='Microsoft' className='social-button' onClick={() => authenticateWithMsal()}>
            {msButtonContent}
          </div>
        ))}
    </>
  )
}
